// @ts-check
// Gemeinsamer Client fuer dimension_geometry.json (Asset Import 2.0-Pipeline-
// Output, siehe cad-converter/app/geometry/dimension_geometry.py, servert
// ueber /api/asset-objects/:id/file/dimensionGeometry). Extrahiert aus
// object_asset_system.js (requestObjectAssetDimensionGeometry()/
// objectAssetDimensionSnapMetadata()), das bisher der einzige Konsument war
// -- jedes native System (SaniGuard, Stupar-A/B, Flexiport-Base, ...) kann
// diesen Helper fuer seine eigenen v2-gebundenen Bauteile nutzen, um
// dimension_service.js's entry.metadata.dimensionSnapSegments/
// dimensionSnapPoints-Vertrag zu befuellen, statt die Fetch-/Skalierungs-
// Logik pro System zu duplizieren.

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { invalidateDrawingObjectSnapshot } from '../objects/drawing_object_snapshot_service.js?v=20260904f';

const DIMENSION_GEOMETRY_CACHE = new Map();

// Liefert die rohe {segments,points}-Geometrie (STEP-mm, bezogen auf die
// Original-Projektionsgroesse) oder null, solange sie (noch) nicht geladen
// ist. Feuert beim ersten Aufruf fuer einen neuen Pfad den Fetch an; ein
// erneuter Aufruf nach erfolgreichem Laden liefert das gecachte Ergebnis
// synchron. Alle wartenden Verbraucher werden bei Erfolg benachrichtigt.
// Die Snapshotinvalidierung erneuert abgeleitete Bemassungs-/Snapdaten ohne
// Modellrevision, History oder Dirty-Status des gespeicherten Dokuments.
export function requestDimensionGeometry(dimensionGeometryPath, { onReady } = {}) {
  const path = String(dimensionGeometryPath || '').trim();
  if (!path) return null;
  const cached = DIMENSION_GEOMETRY_CACHE.get(path);
  if (cached?.status === 'ready') return cached.geometry;
  if (cached?.status === 'loading') {
    if (typeof onReady === 'function') cached.listeners.add(onReady);
    return null;
  }
  if (cached?.status === 'failed') return null;
  const entry = { status: 'loading', geometry: null, listeners: new Set(typeof onReady === 'function' ? [onReady] : []) };
  DIMENSION_GEOMETRY_CACHE.set(path, entry);
  fetch(path, { cache: 'no-store' })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(geometry => {
      entry.status = 'ready';
      entry.geometry = geometry && typeof geometry === 'object' ? geometry : null;
      invalidateDrawingObjectSnapshot();
      for (const listener of entry.listeners) {
        try {
          Promise.resolve(listener()).catch(error => console.warn('Bemassungs-Geometrie-Callback fehlgeschlagen', error));
        } catch (error) {
          console.warn('Bemassungs-Geometrie-Callback fehlgeschlagen', error);
        }
      }
      entry.listeners.clear();
      // A callback may have queried a snapshot while another owner still had
      // old local metadata. The next read sees all successfully notified owners.
      invalidateDrawingObjectSnapshot();
      AppRuntime.requestAppRefresh(false, 'dimension-geometry-ready');
    })
    .catch(error => {
      // Erwartet fuer v1.0-Assets/alte v2.0-Konvertierungen ohne diese Datei
      // (404) -- kein Fehler, das Bauteil bleibt einfach nur ueber die
      // Bounding Box bemassbar.
      entry.status = 'failed';
      entry.listeners.clear();
      console.warn(`Bemassungs-Geometrie konnte nicht geladen werden: ${path}`, error);
      AppRuntime.requestAppRefresh(false, 'dimension-geometry-failed');
    });
  return null;
}

// Skaliert die STEP-mm-Koordinaten aus dimension_geometry.json (bezogen auf
// sourceWidth/sourceDepth, die Originalgroesse der Draufsicht-Projektion)
// auf die tatsaechliche Bauteilgroesse und verschiebt sie um centerX/centerZ
// -- passend zum 'center'-localSpace-Vertrag aus dimension_service.js
// (Punkte werden dort als Punkt-vor-Rotation um metadata.localBounds' bzw.
// metadata.localCenter interpretiert). centerX/centerZ sind bewusst generisch
// gehalten: fuer ein einzelnes platziertes Objekt ist das seine Weltposition,
// fuer ein Bauteil INNERHALB eines groesseren aggregierten Drawing-Objects
// (z.B. eine SaniGuard-Wand) ist es dessen Position im unrotierten
// Lokalraum des uebergeordneten Objects. rotationDeg (optional, Default 0)
// deckt eine dritte Situation ab, die SaniGuard/Stupar-A/B nicht brauchten,
// Flexiport-Base's Stuetzen-Teile aber schon: das einzelne Bauteil hat
// GEGENUEBER dem umschliessenden Drawing-Object noch eine EIGENE Rotation
// (z.B. ein Tiefen-C-Profil, das quer zur Modul-Breitenachse montiert ist) --
// wird VOR der centerX/centerZ-Verschiebung angewendet, damit die Punkte im
// selben (unrotierten) Lokalraum landen wie das umschliessende Objekt sie
// erwartet.
export function computeDimensionSnapMetadata({
  dimensionGeometryPath,
  sourceWidth,
  sourceDepth,
  width,
  depth,
  centerX = 0,
  centerZ = 0,
  rotationDeg = 0,
  onReady
} = {}) {
  const geometry = requestDimensionGeometry(dimensionGeometryPath, { onReady });
  if (!geometry) return null;
  const resolvedSourceWidth = Number(sourceWidth) > 0 ? Number(sourceWidth) : Number(width) || 0;
  const resolvedSourceDepth = Number(sourceDepth) > 0 ? Number(sourceDepth) : Number(depth) || 0;
  const scaleX = resolvedSourceWidth > 0 ? (Number(width) || 0) / resolvedSourceWidth : 1;
  const scaleZ = resolvedSourceDepth > 0 ? (Number(depth) || 0) / resolvedSourceDepth : 1;
  const center = { x: Number(centerX) || 0, z: Number(centerZ) || 0 };
  const rotation = (Number(rotationDeg) || 0) * Math.PI / 180;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const scalePoint = point => {
    const scaledX = (Number(point?.x) || 0) * scaleX;
    const scaledZ = (Number(point?.z) || 0) * scaleZ;
    return {
      x: center.x + scaledX * cos - scaledZ * sin,
      z: center.z + scaledX * sin + scaledZ * cos
    };
  };
  const segments = Array.isArray(geometry.segments) ? geometry.segments : [];
  const points = Array.isArray(geometry.points) ? geometry.points : [];
  return {
    dimensionLocalSpace: 'center',
    dimensionSnapSegments: segments
      .filter(segment => segment?.a && segment?.b)
      .map(segment => ({ a: scalePoint(segment.a), b: scalePoint(segment.b) })),
    dimensionSnapPoints: points
      .filter(Boolean)
      .map(point => ({ ...scalePoint(point), role: point.role || 'center' }))
  };
}
